import React from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { AlertCircle, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface SettingsTabErrorBoundaryProps {
  children: React.ReactNode;
  tabName: string;
}

function TabErrorFallback({ error, resetErrorBoundary, tabName }: FallbackProps & { tabName: string }) {
  return (
    <Card className="border-destructive/50">
      <CardHeader>
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <CardTitle className="text-destructive">Error Loading {tabName}</CardTitle>
        </div>
        <CardDescription>
          An unexpected error occurred while loading this section.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-3 rounded-md bg-muted">
          <p className="text-sm font-mono text-muted-foreground">
            {error.message || 'An unexpected error occurred.'}
          </p>
        </div>
        <Button
          onClick={resetErrorBoundary}
          variant="outline"
          size="sm"
          className="w-full sm:w-auto"
        >
          <RefreshCcw className="mr-2 h-4 w-4" />
          Try Again
        </Button>
      </CardContent>
    </Card>
  );
}

const logTabError = (error: Error, info: React.ErrorInfo, tabName: string) => {
  console.error(`Error in Settings tab "${tabName}":`, error, info);
  // In production, send to error tracking service (Sentry, Datadog, etc.)
};

export const SettingsTabErrorBoundary: React.FC<SettingsTabErrorBoundaryProps> = ({ 
  children, 
  tabName 
}) => {
  return (
    <ErrorBoundary
      FallbackComponent={(props) => <TabErrorFallback {...props} tabName={tabName} />}
      onError={(error, info) => logTabError(error, info, tabName)}
      onReset={() => {
        // Optional: Add any cleanup or state reset logic here
        console.log(`Resetting ${tabName} tab`);
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
